import { MOTION_CHECK_PRELUDE, MOTION_CHECK_STEPS } from "./gcode";
import { LP4FrameParser, STATUS_FRAME, STOP_FRAME } from "./lp4";

// Source: KL-Engraving-App/extension/laser.js.
const BAUD_RATE = 460800;
const USB_FILTERS = [{ usbVendorId: 0x1a86 }];
const BYTE_LOG_LIMIT = 4096;
const DIAGNOSTIC_BYTE_LIMIT = 512;
const STATUS_QUERIES = ["?", "?\n", "?\r\n"];
const LP_STATUS_FRAME = [0xaa, 0xbb, 0x08, 0, 0, 0, 0, 0, 0, 0, 0];
export const PREVIEW_STOP_FRAME = [
  0xaa, 0xbb, 0x11, 0x02, 0x03, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0x04, 0, 0, 0, 0, 0x09,
];

const validatePreviewRectangle = ({ x, y, w, h, power }) => {
  if (!Number.isInteger(power) || power < 1 || power > 10) {
    throw new Error("Preview power must be an integer from 1 to 10");
  }
  if (
    ![x, y, w, h].every(Number.isFinite) ||
    x < 0 ||
    y < 0 ||
    w <= 0 ||
    h <= 0 ||
    x + w > 160 ||
    y + h > 160
  ) {
    throw new Error("Preview rectangle is outside the LP4 work area");
  }
};

const toU16 = (value) => [(value >> 8) & 0xff, value & 0xff];

export const buildPreviewFrame = ({ x, y, w, h, power = 1, diameter = 0 }) => {
  validatePreviewRectangle({ x, y, w, h, power });
  if (!Number.isFinite(diameter) || diameter < 0 || diameter > 655.35) throw new Error("Invalid preview diameter");
  const data = [
    0x02,
    ...toU16(Math.floor(10 * w)),
    ...toU16(Math.floor(10 * h)),
    ...toU16(Math.floor(10 * x)),
    ...toU16(Math.floor(10 * y)),
    0,
    0x04,
    power,
    ...toU16(Math.floor(100 * diameter)),
  ];
  const checksum = (0x02 + data.reduce((sum, byte) => sum + byte, 0)) & 0xffff;
  return new Uint8Array([
    0xaa,
    0xbb,
    data.length + 3,
    0x02,
    ...data,
    (checksum >> 8) & 0xff,
    checksum & 0xff,
  ]);
};

const decodeLatin1 = (bytes) => Array.from(bytes, (byte) => String.fromCharCode(byte)).join("");

const escapeDiagnosticText = (text) => Array.from(text, (character) => {
  const code = character.charCodeAt(0);
  if (code === 13) return "\\r";
  if (code === 10) return "\\n";
  if (code < 32 || (code >= 127 && code <= 159)) return `\\x${code.toString(16).padStart(2, "0")}`;
  return character;
}).join("");

const parseMPos = (status) => {
  const match = status.match(/MPos:([+-]?(?:\d+(?:\.\d*)?|\.\d+)),([+-]?(?:\d+(?:\.\d*)?|\.\d+))/);
  return match ? { x: Number(match[1]), y: Number(match[2]) } : null;
};

const parseCommandedPosition = (line) => {
  const match = line.match(/X([+-]?(?:\d+(?:\.\d*)?|\.\d+))\s+Y([+-]?(?:\d+(?:\.\d*)?|\.\d+))/);
  return match ? { x: Number(match[1]), y: Number(match[2]) } : null;
};

const parseLaserpeckerFrame = (bytes) => {
  let start = -1;
  for (let index = 0; index < bytes.length - 1; index += 1) {
    if (bytes[index] === 0xaa && bytes[index + 1] === 0xbb) {
      start = index;
      break;
    }
  }
  if (start === -1 || bytes.length - start < 3) return null;

  const length = bytes[start + 2];
  if (bytes.length - start - 3 < length) return null;
  const frame = bytes.slice(start, start + length + 3);
  const checksum = frame.slice(3, length + 1).reduce((sum, byte) => sum + byte, 0) & 0xffff;
  const expectedChecksum = (frame[length + 1] << 8) | frame[length + 2];
  return {
    frameHex: Array.from(frame, (byte) => byte.toString(16).padStart(2, "0")).join(" "),
    valid: checksum === expectedChecksum,
    func: frame[3],
    mode: frame[4],
    wState: frame[5],
    error: frame[9],
  };
};

export class Laser {
  constructor() {
    this.port = null;
    this.reader = null;
    this.pending = null;
    this.statusWaiters = [];
    this.frameParser = new LP4FrameParser();
    this.frameWaiters = [];
    this.writeQueue = Promise.resolve();
    this.binaryStatusSupported = false;
    this.fileIdleCheck = null;
    this.statusQuery = null;
    this.statusProbeFailedAt = 0;
    this.idleCheck = null;
    this.rawReplies = [];
    this.okCount = 0;
    this.previewing = false;
    this.previewTimer = null;
    this.previewWritePromise = null;
    this.previewStopPromise = null;
    this.lastPreviewFrame = null;
    this.lastPreviewStopReply = null;
    this.machineState = null;
    this.busy = false;
    this.stopRequested = false;
    this.listeners = new Set();
    this.encoder = new TextEncoder();
    this.buffer = "";
    this.byteLog = [];
    this.totalBytes = 0;
    if (typeof navigator !== "undefined" && navigator.serial) {
      navigator.serial.addEventListener("connect", (event) => {
        if (!this.port) this.connectPort(event.target?.port ?? event.port).catch(() => this.emit());
      });
      navigator.serial.addEventListener("disconnect", (event) => {
        if (event.port === this.port) this.handleDisconnect();
      });
      navigator.serial.getPorts().then((ports) => {
        if (!this.port && ports.length) this.connectPort(ports[0]).catch(() => this.emit());
      }).catch(() => this.emit());
    }
  }

  get connected() {
    return Boolean(this.port?.readable && this.port?.writable);
  }

  getDiagnostics() {
    const bytes = this.byteLog.slice(-DIAGNOSTIC_BYTE_LIMIT);
    let portInfo = null;
    try {
      portInfo = this.port?.getInfo?.() || null;
    } catch {
      portInfo = null;
    }
    return {
      portInfo,
      baudRate: BAUD_RATE,
      dtr: false,
      connected: this.connected,
      busy: this.busy,
      machineState: this.machineState,
      statusQuery: this.statusQuery === null ? null : escapeDiagnosticText(this.statusQuery),
      previewing: this.previewing,
      lastPreviewFrame: this.lastPreviewFrame,
      lastPreviewStopReply: this.lastPreviewStopReply,
      totalBytes: this.totalBytes,
      hex: bytes.map((byte) => byte.toString(16).padStart(2, "0")).join(" "),
      text: escapeDiagnosticText(decodeLatin1(bytes)),
      pendingBuffer: escapeDiagnosticText(this.buffer),
      rawReplies: [...this.rawReplies],
    };
  }

  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  emit() {
    for (const listener of this.listeners) listener(this);
  }

  async requestPort(showAll = false) {
    if (typeof navigator === "undefined" || !navigator.serial) throw new Error("Web Serial is unavailable in this browser");
    const port = await navigator.serial.requestPort(showAll ? {} : { filters: USB_FILTERS });
    await this.connectPort(port);
    return port;
  }

  async connectPort(port) {
    if (!port) throw new Error("No serial port selected");
    if (this.port === port && this.connected) return;
    if (this.previewing) await this.stopPreview();
    if (this.previewTimer !== null) clearTimeout(this.previewTimer);
    this.previewTimer = null;
    this.previewing = false;
    this.previewWritePromise = null;
    this.previewStopPromise = null;
    this.lastPreviewFrame = null;
    this.lastPreviewStopReply = null;
    this.byteLog = [];
    this.totalBytes = 0;
    this.rawReplies = [];
    this.okCount = 0;
    this.machineState = null;
    this.buffer = "";
    this.statusQuery = null;
    this.statusProbeFailedAt = 0;
    this.idleCheck = null;
    this.port = port;
    this.frameParser = new LP4FrameParser();
    this.binaryStatusSupported = false;
    try {
      await port.open({ baudRate: BAUD_RATE });
      try {
        await port.setSignals({ dataTerminalReady: false });
      } catch {
        // Some adapters do not support changing DTR.
      }
      this.reader = port.readable.getReader();
      this.emit();
      this.readLoop(port, this.reader);
    } catch (error) {
      this.port = null;
      this.reader = null;
      this.emit();
      throw error;
    }
  }

  async readLoop(port, reader) {
    try {
      while (port === this.port) {
        const { value, done } = await reader.read();
        if (done) break;
        for (const frame of this.frameParser.push(value)) {
          const waiter = this.frameWaiters.find((candidate) => candidate.matches(frame));
          if (waiter) {
            this.frameWaiters = this.frameWaiters.filter((candidate) => candidate !== waiter);
            clearTimeout(waiter.timer);
            waiter.resolve(frame);
          }
        }
        this.totalBytes += value.byteLength;
        for (const byte of value) this.byteLog.push(byte);
        if (this.byteLog.length > BYTE_LOG_LIMIT) this.byteLog.splice(0, this.byteLog.length - BYTE_LOG_LIMIT);
        this.buffer += decodeLatin1(value);
        const lines = this.buffer.split(/\r\n|\r|\n/);
        this.buffer = (lines.pop() ?? "").slice(-BYTE_LOG_LIMIT);
        for (const line of lines) this.processLine(line);
      }
    } catch {
      if (port === this.port) this.handleDisconnect();
    } finally {
      try {
        reader.releaseLock();
      } catch {
        // Reader may already be unlocked after a disconnect.
      }
      if (port === this.port) this.handleDisconnect();
    }
  }

  processLine(line) {
    if (!line || !line.trim()) return;
    this.rawReplies.push(line);
    if (this.rawReplies.length > 100) this.rawReplies.shift();
    const status = line.match(/<\s*([A-Za-z]+)(?::\d+)?(?=[|,>:]|$)/);
    if (status) {
      this.machineState = status[1];
      const waiters = this.statusWaiters.splice(0);
      for (const waiter of waiters) {
        if (waiter.expected === this.machineState || waiter.expected === "*") {
          clearTimeout(waiter.timer);
          waiter.resolve(line);
        } else {
          this.statusWaiters.push(waiter);
        }
      }
      this.emit();
      return;
    }
    const reply = line.trim();
    if (reply === "ok") {
      this.okCount += 1;
      this.resolvePending();
      return;
    }
    if (/^(error:\d+|ALARM:\d+)$/i.test(reply)) {
      this.rejectPending(new Error(reply));
      this.emit();
    }
  }

  resolvePending() {
    if (!this.pending) return;
    const { resolve, timer } = this.pending;
    clearTimeout(timer);
    this.pending = null;
    resolve();
  }

  rejectPending(error) {
    if (!this.pending) return;
    const { reject, timer } = this.pending;
    clearTimeout(timer);
    this.pending = null;
    reject(error);
  }

  writeBytes(bytes, shouldWrite = () => true) {
    const write = this.writeQueue.then(async () => {
      if (!this.connected) throw new Error("Laser not connected");
      if (!shouldWrite()) throw new Error("Engraving stopped");
      const writer = this.port.writable.getWriter();
      try {
        await writer.write(bytes);
      } finally {
        writer.releaseLock();
      }
    });
    this.writeQueue = write.catch(() => {});
    return write;
  }

  waitForFrame(matches, timeoutMs, label) {
    let waiter;
    const promise = new Promise((resolve, reject) => {
      waiter = { matches, resolve, reject, timer: setTimeout(() => {
        this.frameWaiters = this.frameWaiters.filter((candidate) => candidate !== waiter);
        reject(new Error(`LP4 ${label} timed out after ${timeoutMs / 1000} s`));
      }, timeoutMs) };
      this.frameWaiters.push(waiter);
    });
    promise.cancel = () => {
      clearTimeout(waiter.timer);
      this.frameWaiters = this.frameWaiters.filter((candidate) => candidate !== waiter);
      waiter.resolve(null);
    };
    promise.catch(() => {});
    return promise;
  }

  rejectFrameWaiters(error) {
    for (const waiter of this.frameWaiters.splice(0)) {
      clearTimeout(waiter.timer);
      waiter.reject(error);
    }
  }

  async requestFrame(bytes, timeoutMs, label, matches = () => true, shouldWrite) {
    const response = this.waitForFrame(matches, timeoutMs, label);
    try {
      const [, frame] = await Promise.all([this.writeBytes(bytes, shouldWrite), response]);
      return frame;
    } finally {
      response.cancel();
    }
  }

  async isFileIdle() {
    if (!this.connected || this.busy) return false;
    if (this.fileIdleCheck) return this.fileIdleCheck;
    const check = (async () => {
      const frame = await this.requestFrame(STATUS_FRAME, 3000, "idle status", (data) => data[3] === 0 && data.length >= 12);
      this.binaryStatusSupported = true;
      const idle = frame[4] === 6 && frame[5] !== 4 && frame[9] === 0;
      this.machineState = idle ? "Idle" : "Run";
      this.emit();
      return idle;
    })();
    this.fileIdleCheck = check;
    try { return await check; } finally { if (this.fileIdleCheck === check) this.fileIdleCheck = null; }
  }

  async writeRaw(value) {
    await this.writeBytes(this.encoder.encode(value));
  }

  async writeLine(line, timeoutMs = 10000) {
    if (this.pending) throw new Error("Laser is already waiting for a response");
    let resolveResponse;
    let rejectResponse;
    const response = new Promise((resolve, reject) => {
      resolveResponse = resolve;
      rejectResponse = reject;
    });
    const timer = setTimeout(() => {
      this.rejectPending(new Error(`Timed out waiting for response to ${line}`));
    }, timeoutMs);
    this.pending = { resolve: resolveResponse, reject: rejectResponse, timer };
    try {
      await this.writeRaw(`${line}\n`);
      await response;
    } catch (error) {
      if (this.pending?.reject === rejectResponse) this.rejectPending(error);
      throw error;
    }
  }

  waitForStatus(expected, timeoutMs = 2000) {
    let waiter;
    let resolveWaiter;
    const promise = new Promise((resolve, reject) => {
      resolveWaiter = resolve;
      waiter = {
        expected,
        resolve,
        reject,
        timer: setTimeout(() => {
          this.statusWaiters = this.statusWaiters.filter((candidate) => candidate !== waiter);
          reject(new Error(`Timed out waiting for laser status ${expected}`));
        }, timeoutMs),
      };
      this.statusWaiters.push(waiter);
    });
    promise.cancel = () => {
      if (!this.statusWaiters.includes(waiter)) return;
      clearTimeout(waiter.timer);
      this.statusWaiters = this.statusWaiters.filter((candidate) => candidate !== waiter);
      resolveWaiter(null);
    };
    return promise;
  }

  async queryStatus(query, timeoutMs = 1500) {
    const startedAt = Date.now();
    const bytesBefore = this.totalBytes;
    const statusWait = this.waitForStatus("*", timeoutMs);
    let outcome;
    let ok = false;
    try {
      await this.writeRaw(query);
      const status = await statusWait;
      outcome = `status ${status}`;
      ok = true;
    } catch (error) {
      statusWait.cancel();
      const message = error.message || String(error);
      outcome = message.startsWith("Timed out waiting for laser status") ? "timeout" : `write failed: ${message}`;
    }
    return {
      query,
      outcome,
      ms: Date.now() - startedAt,
      bytes: this.totalBytes - bytesBefore,
      ok,
    };
  }

  async probeStatusQuery() {
    const results = [];
    this.statusQuery = null;
    for (const query of STATUS_QUERIES) {
      results.push(await this.queryStatus(query));
    }
    this.statusQuery = results.find((result) => result.ok)?.query || null;
    this.statusProbeFailedAt = this.statusQuery === null ? Date.now() : 0;
    return results;
  }

  async queryBinaryStatus(timeoutMs = 1000) {
    const startedAt = Date.now();
    const startIndex = this.totalBytes;
    const byteLogLengthAtStart = this.byteLog.length;
    let receivedBytes = [];
    let frame = null;
    let writeError = null;
    const bytesSinceStart = () => {
      const byteCount = Math.min(Math.max(0, this.totalBytes - startIndex), this.byteLog.length);
      const tailStart = this.byteLog.length - byteCount;
      return this.byteLog.slice(Math.min(byteLogLengthAtStart, tailStart));
    };

    try {
      await this.writeBytes(new Uint8Array(LP_STATUS_FRAME));
      while (Date.now() - startedAt < timeoutMs) {
        receivedBytes = bytesSinceStart();
        frame = parseLaserpeckerFrame(receivedBytes);
        if (frame) break;
        await new Promise((resolve) => setTimeout(resolve, 50));
      }
      if (!frame) {
        receivedBytes = bytesSinceStart();
        frame = parseLaserpeckerFrame(receivedBytes);
      }
    } catch (error) {
      writeError = error.message || String(error);
    }

    await new Promise((resolve) => setTimeout(resolve, 100));
    this.buffer = "";
    receivedBytes = bytesSinceStart();
    const bytes = Math.max(0, this.totalBytes - startIndex);
    const ms = Date.now() - startedAt;
    const query = "LP binary status (aa bb 08 00…)";
    if (writeError) return { query, outcome: `write failed: ${writeError}`, ms, bytes, ok: false };
    if (frame) {
      return {
        query,
        outcome: `frame ${frame.frameHex} (checksum ${frame.valid ? "ok" : "bad"}) mode=${frame.mode} w_state=${frame.wState} error=${frame.error}`,
        ms,
        bytes,
        ok: frame.valid,
      };
    }
    if (receivedBytes.length) {
      const hex = Array.from(receivedBytes, (byte) => byte.toString(16).padStart(2, "0")).join(" ");
      return { query, outcome: `no frame: ${hex}`, ms, bytes, ok: false };
    }
    return { query, outcome: "timeout", ms, bytes, ok: false };
  }

  async isIdle() {
    if (this.binaryStatusSupported) return this.isFileIdle();
    if (!this.connected || this.busy) return false;
    if (this.idleCheck) return this.idleCheck;
    const idleCheck = (async () => {
      if (this.statusQuery === null) {
        if (Date.now() - this.statusProbeFailedAt < 10000) return false;
        await this.probeStatusQuery();
        if (this.statusQuery === null) return false;
      }
      const result = await this.queryStatus(this.statusQuery, 2000);
      return result.ok && this.machineState === "Idle";
    })();
    this.idleCheck = idleCheck;
    try {
      return await idleCheck;
    } finally {
      if (this.idleCheck === idleCheck) this.idleCheck = null;
    }
  }

  async waitUntilIdle({ timeoutMs = 120000, intervalMs = 250 } = {}) {
    if (!this.connected) throw new Error("Laser not connected");
    const startedAt = Date.now();

    while (Date.now() - startedAt < timeoutMs) {
      if (!this.connected) throw new Error("Laser disconnected");
      if (this.statusQuery === null) {
        if (Date.now() - this.statusProbeFailedAt >= 10000) await this.probeStatusQuery();
      }
      if (!this.busy && this.statusQuery !== null) {
        const remaining = timeoutMs - (Date.now() - startedAt);
        if (remaining <= 0) break;
        const result = await this.queryStatus(this.statusQuery, Math.min(2000, remaining));
        if (result.ok) {
          const status = result.outcome.slice("status ".length);
          const state = status.match(/<\s*([A-Za-z]+)(?::\d+)?(?=[|,>:]|$)/)?.[1];
          if (state === "Idle") {
            const mpos = status.match(/MPos:([+-]?(?:\d+(?:\.\d*)?|\.\d+)),([+-]?(?:\d+(?:\.\d*)?|\.\d+))/);
            return {
              status,
              mpos: mpos ? { x: Number(mpos[1]), y: Number(mpos[2]) } : null,
              waitedMs: Date.now() - startedAt,
            };
          }
        }
      }
      const remaining = timeoutMs - (Date.now() - startedAt);
      if (remaining <= 0) break;
      await new Promise((resolve) => setTimeout(resolve, Math.min(intervalMs, remaining)));
    }

    throw new Error("Timed out waiting for laser status Idle");
  }

  async readInfo() {
    if (!this.connected) throw new Error("Laser not connected");
    if (this.busy) throw new Error("Laser is engraving");
    const queries = [];
    const startedAt = Date.now();
    const bytesBefore = this.totalBytes;
    let outcome = "ok";
    try {
      await this.writeLine("$I", 1500);
    } catch (error) {
      const message = error.message || String(error);
      const replyError = message.match(/^(error:\d+|ALARM:\d+)/i);
      if (replyError) outcome = replyError[0];
      else if (message.startsWith("Timed out waiting for response")) outcome = "timeout";
      else outcome = `write failed: ${message}`;
    }
    queries.push({
      query: "$I",
      outcome,
      ms: Date.now() - startedAt,
      bytes: this.totalBytes - bytesBefore,
    });

    const statusQueries = await this.probeStatusQuery();
    queries.push(...statusQueries.map((result) => ({
      ...result,
      query: escapeDiagnosticText(result.query),
    })));
    queries.push(await this.queryBinaryStatus());
    return { queries, statusQuery: this.statusQuery, ...this.getDiagnostics() };
  }

  async startPreview({ x, y, w, h, power = 1, diameter = 0, maxMs = 30000 }) {
    if (!this.connected) throw new Error("Laser not connected");
    if (this.busy) throw new Error("Laser is already busy");
    if (!Number.isFinite(maxMs) || maxMs <= 0) {
      throw new Error("Preview duration must be greater than zero");
    }
    const frame = buildPreviewFrame({ x, y, w, h, power, diameter });
    if (!await this.isIdle()) throw new Error("Laser must be idle before preview");
    if (!this.connected) throw new Error("Laser not connected");
    if (this.busy) throw new Error("Laser is already busy");

    this.busy = true;
    this.previewing = true;
    this.lastPreviewStopReply = null;
    this.emit();
    try {
      this.previewWritePromise = this.writeBytes(frame);
      await this.previewWritePromise;
      this.previewWritePromise = null;
      if (!this.connected || !this.previewing) throw new Error("Laser disconnected");
      this.lastPreviewFrame = Array.from(frame, (byte) => byte.toString(16).padStart(2, "0")).join(" ");
      this.previewTimer = setTimeout(() => {
        this.stopPreview().catch(() => {});
      }, maxMs);
      this.emit();
    } catch (error) {
      if (this.previewTimer !== null) clearTimeout(this.previewTimer);
      this.previewTimer = null;
      this.previewWritePromise = null;
      this.previewing = false;
      this.busy = false;
      this.emit();
      throw error;
    }
  }

  async stopPreview() {
    if (!this.previewing) return;
    if (this.previewStopPromise) return this.previewStopPromise;

    const stop = async () => {
      if (this.previewTimer !== null) clearTimeout(this.previewTimer);
      this.previewTimer = null;
      let frame = null;
      try {
        if (this.previewWritePromise) await this.previewWritePromise;
        if (!this.connected) throw new Error("Laser not connected");

        const startIndex = this.totalBytes;
        const byteLogLengthAtStart = this.byteLog.length;
        const bytesSinceStart = () => {
          const byteCount = Math.min(Math.max(0, this.totalBytes - startIndex), this.byteLog.length);
          const tailStart = this.byteLog.length - byteCount;
          return this.byteLog.slice(Math.min(byteLogLengthAtStart, tailStart));
        };

        await this.writeBytes(new Uint8Array(PREVIEW_STOP_FRAME));
        const deadline = Date.now() + 3000;
        while (Date.now() < deadline) {
          frame = parseLaserpeckerFrame(bytesSinceStart());
          if (frame) break;
          await new Promise((resolve) => setTimeout(resolve, 50));
        }
        if (!frame) frame = parseLaserpeckerFrame(bytesSinceStart());
        this.lastPreviewStopReply = frame ? frame.frameHex : "no reply";
      } catch (error) {
        this.lastPreviewStopReply = "no reply";
        throw error;
      } finally {
        await new Promise((resolve) => setTimeout(resolve, 100));
        this.buffer = "";
        this.previewing = false;
        this.busy = false;
        this.previewWritePromise = null;
        this.previewTimer = null;
        this.emit();
      }
    };

    this.previewStopPromise = stop();
    try {
      await this.previewStopPromise;
    } finally {
      this.previewStopPromise = null;
    }
  }

  async motionCheck() {
    if (!this.connected) throw new Error("Laser not connected");
    if (this.busy) throw new Error("Laser is already engraving");

    const startedAt = new Date().toISOString();
    const startedAtMs = Date.now();
    const prelude = [];
    const steps = [];
    this.busy = true;
    this.stopRequested = false;
    this.emit();

    try {
      if (this.statusQuery === null) await this.probeStatusQuery();
      if (this.stopRequested) throw new Error("Engraving stopped");
      if (this.statusQuery === null) throw new Error("No laser status query answered");

      const baseline = await this.queryStatus(this.statusQuery, 1000);
      if (this.stopRequested) throw new Error("Engraving stopped");
      if (!baseline.ok || this.machineState !== "Idle") {
        throw new Error("Motion check requires a fresh Idle baseline");
      }
      await new Promise((resolve) => setTimeout(resolve, 200));

      for (const line of MOTION_CHECK_PRELUDE) {
        if (this.stopRequested) throw new Error("Engraving stopped");
        const lineStartedAt = Date.now();
        try {
          await this.writeLine(line, 3000);
          prelude.push({ line, okMs: Date.now() - lineStartedAt });
        } catch (error) {
          prelude.push({ line, error: error.message || String(error) });
          throw error;
        }
      }

      for (const step of MOTION_CHECK_STEPS) {
        if (this.stopRequested) throw new Error("Engraving stopped");
        const t0 = Date.now();
        await this.writeLine(step.line, 10000);
        const okMs = Date.now() - t0;
        const okCountBeforePolling = this.okCount;
        const samples = [];
        const commanded = parseCommandedPosition(step.line);
        let lastStatus = null;
        let previousIdleMpos = null;
        let previousWasIdle = false;
        let pollCount = 0;

        while (pollCount === 0 || Date.now() - t0 < step.settleMs) {
          if (this.stopRequested) throw new Error("Engraving stopped");
          const result = await this.queryStatus(this.statusQuery, 1000);
          if (this.stopRequested) throw new Error("Engraving stopped");
          if (!result.ok && result.outcome.startsWith("write failed:")) {
            throw new Error(result.outcome);
          }

          const status = result.ok ? result.outcome.slice("status ".length) : result.outcome;
          const sampleMs = Date.now() - t0;
          const previousSample = samples[samples.length - 1];
          if (previousSample?.status === status) {
            previousSample.untilMs = sampleMs;
          } else {
            samples.push({ ms: sampleMs, untilMs: sampleMs, status });
          }
          pollCount += 1;
          lastStatus = result.ok ? status : null;

          const state = status.match(/<\s*([A-Za-z]+)(?::\d+)?(?=[|,>:]|$)/)?.[1];
          const mpos = result.ok ? parseMPos(status) : null;
          if (
            state === "Idle" &&
            mpos &&
            previousWasIdle &&
            previousIdleMpos.x === mpos.x &&
            previousIdleMpos.y === mpos.y
          ) {
            break;
          }
          previousWasIdle = state === "Idle" && Boolean(mpos);
          previousIdleMpos = previousWasIdle ? mpos : null;

          if (Date.now() - t0 < step.settleMs) {
            await new Promise((resolve) => setTimeout(resolve, 100));
          }
        }

        await new Promise((resolve) => setTimeout(resolve, 200));
        if (this.stopRequested) throw new Error("Engraving stopped");

        const reportedMPos = lastStatus ? parseMPos(lastStatus) : null;
        steps.push({
          line: step.line,
          settleMs: step.settleMs,
          okMs,
          samples,
          strayOk: this.okCount - okCountBeforePolling,
          commanded,
          reportedMPos,
          dx: commanded && reportedMPos ? reportedMPos.x - commanded.x : null,
          dy: commanded && reportedMPos ? reportedMPos.y - commanded.y : null,
        });
      }

      return {
        startedAt,
        statusQuery: escapeDiagnosticText(this.statusQuery),
        baseline,
        prelude,
        steps,
        totalMs: Date.now() - startedAtMs,
      };
    } catch (error) {
      await this.stopSequence();
      throw error;
    } finally {
      this.busy = false;
      this.emit();
    }
  }

  async run(job, onProgress = () => {}) {
    if (!this.connected) throw new Error("Laser not connected");
    if (this.busy) throw new Error("Laser is already engraving");
    if (!(job?.header instanceof Uint8Array) || job.header.length !== 64
      || !(job.payload instanceof Uint8Array) || !job.payload.length || job.payload.length % 6 !== 0
      || job.header[0] !== 0x40 || !(job.startFrame instanceof Uint8Array) || !(job.beginFrame instanceof Uint8Array)) {
      throw new Error("A valid LP4 fill file is required; GRBL engraving streaming is disabled");
    }
    this.busy = true;
    this.stopRequested = false;
    this.emit();
    try {
      const active = () => !this.stopRequested;
      const assertActive = () => { if (!active()) throw new Error("Engraving stopped"); };
      await this.requestFrame(STOP_FRAME, 3000, "stop reply", () => true, active);
      assertActive();
      const begin = await this.requestFrame(job.beginFrame, 30000, "upload begin", () => true, active);
      if (begin[5] !== 1) throw new Error(`LP4 rejected upload begin (reply ${begin[5] ?? "missing"})`);
      assertActive();
      const finalReply = this.waitForFrame(() => true, 60000, "upload completion");
      let transferError = null;
      const transferFailure = new Promise((resolve, reject) => {
        finalReply.then((frame) => {
          if (frame && frame[5] !== 1) {
            transferError = new Error(`LP4 rejected uploaded file (reply ${frame[5] ?? "missing"})`);
            reject(transferError);
          }
        }, (error) => { transferError = error; reject(error); });
      });
      transferFailure.catch(() => {});
      try {
        for (const data of [job.header, job.payload]) {
          for (let offset = 0; offset < data.length; offset += 2048) {
            assertActive();
            if (transferError) throw transferError;
            await Promise.race([this.writeBytes(data.slice(offset, offset + 2048), active), transferFailure]);
            await new Promise((resolve) => setTimeout(resolve, 50));
            if (transferError) throw transferError;
          }
        }
        const final = await finalReply;
        if (final[5] !== 1) throw new Error(`LP4 rejected uploaded file (reply ${final[5] ?? "missing"})`);
      } finally {
        finalReply.cancel();
      }
      assertActive();
      await this.writeBytes(job.startFrame, active);
      const startedAt = Date.now();
      let startObserved = false;
      while (true) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        assertActive();
        const status = await this.requestFrame(STATUS_FRAME, 3000, "job status", (frame) => frame[3] === 0 && frame.length >= 12, active);
        assertActive();
        if (status[4] === 5) {
          throw new Error(`LP4 job error ${status[9]}${status[9] === 3 ? ": over-temperature" : ""}`);
        }
        if (status[4] !== 6 || status[5] === 4) startObserved = true;
        if (status[4] === 6 && status[5] !== 4 && (startObserved || Date.now() - startedAt >= 15000)) {
          this.machineState = "Idle";
          onProgress(1);
          return { mode: status[4], wState: status[5], error: status[9], frame: Array.from(status), startObserved };
        }
        this.machineState = status[5] === 4 ? "Hold" : "Run";
        onProgress(Math.min(0.99, status[6] / 100));
      }
    } catch (error) {
      if (!this.stopRequested) this.stopSequence().catch(() => {});
      throw new Error(`LP4 file job failed: ${error.message}`);
    } finally {
      this.busy = false;
      this.emit();
    }
  }

  async stopSequence() {
    this.stopRequested = true;
    this.rejectPending(new Error("Engraving stopped"));
    this.rejectFrameWaiters(new Error("Engraving stopped"));
    if (this.connected) await this.writeBytes(STOP_FRAME);
    this.emit();
  }

  async stop() {
    if (this.previewing) {
      try {
        await this.stopPreview();
      } finally {
        await this.stopSequence();
      }
      return;
    }
    if (!this.connected) {
      this.stopRequested = true;
      this.rejectPending(new Error("Engraving stopped"));
      this.rejectFrameWaiters(new Error("Engraving stopped"));
      return;
    }
    await this.stopSequence();
  }

  handleDisconnect() {
    this.rejectFrameWaiters(new Error("Laser disconnected"));
    this.rejectPending(new Error("Laser disconnected"));
    for (const waiter of this.statusWaiters.splice(0)) {
      clearTimeout(waiter.timer);
      waiter.reject(new Error("Laser disconnected"));
    }
    this.port = null;
    this.reader = null;
    if (this.previewTimer !== null) clearTimeout(this.previewTimer);
    this.previewTimer = null;
    if (this.previewing) this.lastPreviewStopReply = "no reply";
    if (this.previewing) this.busy = false;
    this.previewing = false;
    this.previewWritePromise = null;
    this.machineState = null;
    this.statusQuery = null;
    this.statusProbeFailedAt = 0;
    this.idleCheck = null;
    this.fileIdleCheck = null;
    this.binaryStatusSupported = false;
    this.emit();
  }
}
